import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Input, Textarea, CustomSelect, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError } from '@redpanda/ui';
import { createLeaveRequest, updateLeaveRequest, getLeaveQuota } from '../services/api';
import { LeaveRequest, useLeaveStoreBase } from '../stores/attendanceStore';
import { useEmployeeOptionsStore, employeeOptions } from '../stores/employeeStore';
import { notifyDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';
import { removeListItem } from '../utils/pendingItem';
import { dateInputValue, ymd } from '../utils/attendance';
import { useUnsavedChangesWarning } from '@redpanda/utils';

interface LeaveRequestDialogProps {
  leaveRequest?: LeaveRequest;
  onClose: () => void;
}

interface FormData {
  employeeId: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  days: string;
  reason: string;
}

interface LeaveQuota {
  yearsOfService: number;
  quotas: {
    annual: { total: number; used: number; remaining: number };
    sick: { total: number; used: number; remaining: number };
    personal: { used: number };
  };
}

export const LEAVE_TYPES = ['ANNUAL', 'SICK', 'PERSONAL', 'MARRIAGE', 'BEREAVEMENT', 'MATERNITY', 'PATERNITY', 'OTHER'];

const DAY_MS = 24 * 60 * 60 * 1000;
/** 起訖日（含頭尾）的日曆天數 */
const daysBetween = (start: string, end: string) =>
  start && end && end >= start ? Math.round((Date.parse(end) - Date.parse(start)) / DAY_MS) + 1 : 0;

const emptyForm = (): FormData => {
  const today = ymd(new Date());
  return { employeeId: '', leaveType: 'ANNUAL', startDate: today, endDate: today, days: '1', reason: '' };
};

/** 新增／編輯請假（只有待審核的可以編輯）：樂觀更新＋立即關閉 */
const LeaveRequestDialog: React.FC<LeaveRequestDialogProps> = ({ leaveRequest, onClose }) => {
  const { t } = useTranslation(['pages', 'translation']);
  const isEditMode = !!leaveRequest;
  const { employees, fetchAll } = useEmployeeOptionsStore();
  const { register, handleSubmit, formState: { errors, isDirty, dirtyFields }, setValue, reset, watch } = useForm<FormData>({ defaultValues: emptyForm() });
  const [quota, setQuota] = useState<LeaveQuota | null>(null);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useLayoutEffect(() => {
    reset(leaveRequest ? {
      employeeId: leaveRequest.employeeId,
      leaveType: leaveRequest.leaveType,
      startDate: dateInputValue(leaveRequest.startDate),
      endDate: dateInputValue(leaveRequest.endDate),
      days: String(leaveRequest.days),
      reason: leaveRequest.reason || '',
    } : emptyForm());
  }, [leaveRequest, reset]);

  const { handleClose, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty, onClose);

  const employeeId = watch('employeeId');
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  // 選了員工就顯示今年的假別額度（每次換人重抓，不快取）
  useEffect(() => {
    if (!employeeId) { setQuota(null); return; }
    let cancelled = false;
    getLeaveQuota(employeeId)
      .then(response => { if (!cancelled) setQuota(response.data); })
      .catch(() => { if (!cancelled) setQuota(null); });
    return () => { cancelled = true; };
  }, [employeeId]);

  // 起訖日改了就重算天數（使用者自己改過天數，例如半天，就不蓋掉；改日期時重新計算）
  useEffect(() => {
    if (!dirtyFields.startDate && !dirtyFields.endDate) return;
    const days = daysBetween(startDate, endDate);
    if (days > 0) setValue('days', String(days), { shouldDirty: true });
  }, [startDate, endDate, dirtyFields.startDate, dirtyFields.endDate, setValue]);

  const onSubmit = (data: FormData) => {
    const payload = {
      employeeId: data.employeeId,
      leaveType: data.leaveType,
      startDate: `${data.startDate}T00:00:00.000Z`,
      endDate: `${data.endDate}T00:00:00.000Z`,
      days: Number(data.days),
      reason: data.reason.trim(),
    };
    const selected = employees.find(e => e.id === data.employeeId);
    const employee = selected ? { id: selected.id, name: selected.name, department: selected.department } : leaveRequest?.employee;
    const store = useLeaveStoreBase.getState();
    onClose();

    if (leaveRequest) {
      const original = { ...leaveRequest };
      store.updateItem(leaveRequest.id, { ...payload, employee });
      updateLeaveRequest(leaveRequest.id, payload)
        .then(response => {
          useLeaveStoreBase.getState().updateItem(leaveRequest.id, response.data);
          toast.success(t('pages:attendance.leave.edit.success'));
          notifyDataChanged('leaves');
        })
        .catch(error => {
          useLeaveStoreBase.getState().updateItem(leaveRequest.id, original);
          toast.error(apiErrorMessage(error, 'translation:common.messages.updateFailed'));
        });
      return;
    }

    const tempId = `temp-${Date.now()}`;
    store.addItem({ ...payload, id: tempId, status: 'PENDING', employee });
    createLeaveRequest(payload)
      .then(response => {
        useLeaveStoreBase.getState().updateItem(tempId, response.data);
        toast.success(t('pages:attendance.leave.add.success'));
        notifyDataChanged('leaves');
      })
      .catch(error => {
        removeListItem(useLeaveStoreBase, tempId);
        toast.error(apiErrorMessage(error, 'translation:common.messages.createFailed'));
      });
  };

  return (
    <>
      <DialogShell onClose={handleClose} className="max-w-xl">
        <DialogHeader
          title={isEditMode ? t('pages:attendance.leave.edit.title') : t('pages:attendance.leave.add.title')}
          onClose={handleClose}
          closeLabel={t('translation:common.actions.close')}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
            <div className="sm:col-span-2">
              <FormFieldWithError label={t('pages:attendance.leave.form.employee')} required error={errors.employeeId?.message} htmlFor="employeeId">
                <input type="hidden" {...register('employeeId', { required: t('pages:attendance.leave.form.selectEmployee') })} />
                <CustomSelect
                  id="employeeId"
                  value={employeeId}
                  onChange={value => setValue('employeeId', value, { shouldDirty: true, shouldValidate: true })}
                  variant={errors.employeeId ? 'error' : 'default'}
                  searchable
                  placeholder={t('pages:attendance.leave.form.selectEmployeePlaceholder')}
                  options={employeeOptions(employees, leaveRequest?.employeeId)}
                />
              </FormFieldWithError>

              {/* 今年額度：中性細框＋清單，不用彩色資訊色塊（§3-17） */}
              {quota && (
                <dl className="mt-3 grid grid-cols-3 divide-x divide-line rounded-md border border-line text-[13px]">
                  {([
                    ['annual', `${quota.quotas.annual.remaining} / ${quota.quotas.annual.total}`],
                    ['sick', `${quota.quotas.sick.remaining} / ${quota.quotas.sick.total}`],
                    ['personalUsed', String(quota.quotas.personal.used)],
                  ] as const).map(([key, value]) => (
                    <div key={key} className="px-3 py-2">
                      <dt className="text-fg-secondary">{t(`pages:attendance.leave.quota.${key}`)}</dt>
                      <dd className="mt-0.5 font-medium text-fg tabular-nums">{t('pages:attendance.leave.quota.daysValue', { value })}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>

            <FormFieldWithError label={t('pages:attendance.leave.form.leaveType')} required htmlFor="leaveType">
              <CustomSelect
                id="leaveType"
                value={watch('leaveType')}
                onChange={value => setValue('leaveType', value, { shouldDirty: true })}
                options={LEAVE_TYPES.map(value => ({ value, label: t(`pages:attendance.leave.types.${value}`) }))}
              />
            </FormFieldWithError>
            <FormFieldWithError label={t('pages:attendance.leave.form.days')} required error={errors.days?.message} helpText={t('pages:attendance.leave.form.daysHint')} htmlFor="days">
              <Input
                id="days"
                type="number"
                step="0.5"
                min="0.5"
                inputMode="decimal"
                {...register('days', {
                  required: t('pages:attendance.leave.form.enterDays'),
                  min: { value: 0.5, message: t('pages:attendance.leave.form.minDays') },
                  max: { value: 365, message: t('pages:attendance.leave.form.maxDays') },
                })}
                variant={errors.days ? 'error' : 'default'}
              />
            </FormFieldWithError>
            <FormFieldWithError label={t('pages:attendance.leave.form.startDate')} required error={errors.startDate?.message} htmlFor="startDate">
              <Input id="startDate" type="date" {...register('startDate', { required: t('pages:attendance.leave.form.selectStartDate') })} variant={errors.startDate ? 'error' : 'default'} />
            </FormFieldWithError>
            <FormFieldWithError label={t('pages:attendance.leave.form.endDate')} required error={errors.endDate?.message} htmlFor="endDate">
              <Input
                id="endDate"
                type="date"
                {...register('endDate', {
                  required: t('pages:attendance.leave.form.selectEndDate'),
                  validate: value => !startDate || value >= startDate || t('pages:attendance.leave.form.endDateValidation'),
                })}
                variant={errors.endDate ? 'error' : 'default'}
              />
            </FormFieldWithError>
            <div className="sm:col-span-2">
              <FormFieldWithError label={t('pages:attendance.leave.form.reason')} error={errors.reason?.message} htmlFor="reason">
                <Textarea
                  id="reason"
                  rows={3}
                  {...register('reason', { maxLength: { value: 500, message: t('pages:attendance.leave.form.reasonMaxLength') } })}
                  variant={errors.reason ? 'error' : 'default'}
                />
              </FormFieldWithError>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={handleClose} className={dialogButtonClass.cancel}>
              {t('translation:common.actions.cancel')}
            </button>
            <button type="submit" className={dialogButtonClass.primary}>
              {isEditMode ? t('translation:common.actions.save') : t('pages:attendance.leave.form.submit')}
            </button>
          </DialogFooter>
        </form>
      </DialogShell>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('translation:components.modal.unsavedChanges.title')}
        message={t('translation:components.modal.unsavedChanges.message')}
        confirmText={t('translation:components.modal.unsavedChanges.confirmButton')}
        cancelText={t('translation:components.modal.unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default LeaveRequestDialog;
